import React from "react";
import SEOHead from "@/components/site/SEOHead";
import BlogLayout from "@/components/site/BlogLayout";

// --- SEO constants ----------------------------------------------------------
const CANONICAL = "https://emgroupdigital.fr/blog/cout-site-web-maurice";
const CANONICAL_EN = "https://emgroupdigital.fr/blog/website-cost-mauritius";
const TITLE = "Combien coûte un site web à Maurice en 2026 ? — EM Group";
const DESCRIPTION =
  "Découvrez le prix d'un site web professionnel à Maurice en 2026. Achat ou location à partir de Rs 3 500/mois — EM Group vous explique tout.";
const KEYWORDS =
  "coût site web Maurice, prix site internet Maurice, tarif site web Maurice 2026, site web pas cher Maurice, location site web Maurice, création site web Maurice, agence web Maurice";
const OG_IMAGE = "https://emgroupdigital.fr/brand/logo-em-group.png";
const DATE_PUBLISHED = "2026-09-04";

const ARTICLE_SCHEMA = {
  headline: "Combien coûte un site web à Maurice en 2026 ?",
  description: DESCRIPTION,
  image: OG_IMAGE,
  inLanguage: "fr-MU",
  datePublished: DATE_PUBLISHED,
  dateModified: DATE_PUBLISHED,
  canonical: CANONICAL,
  section: "Guide site web Maurice",
  keywords: KEYWORDS,
};

const BREADCRUMB = [
  { name: "Accueil", item: "https://emgroupdigital.fr/" },
  { name: "Blog", item: "https://emgroupdigital.fr/blog" },
  { name: "Combien coûte un site web à Maurice", item: CANONICAL },
];

const ALTERNATES = [
  { hreflang: "fr", href: CANONICAL },
  { hreflang: "fr-MU", href: CANONICAL },
  { hreflang: "en", href: CANONICAL_EN },
  { hreflang: "en-MU", href: CANONICAL_EN },
  { hreflang: "x-default", href: CANONICAL },
];

// --- Page -------------------------------------------------------------------
export default function BlogCoutSiteWebMaurice() {
  return (
    <>
      <SEOHead
        title={TITLE}
        description={DESCRIPTION}
        keywords={KEYWORDS}
        canonical={CANONICAL}
        ogImage={OG_IMAGE}
        ogType="article"
        locale="fr_MU"
        htmlLang="fr"
        robots="index, follow"
        articleSchema={ARTICLE_SCHEMA}
        breadcrumbSchema={BREADCRUMB}
        alternates={ALTERNATES}
      />

      <BlogLayout
        testId="blog-cout-site-web-maurice"
        kicker="Guide · Maurice"
        title="Combien coûte un site web à Maurice en 2026 ?"
        lede="Si vous avez déjà demandé un devis pour un site web à Maurice, vous avez probablement reçu une facture qui commence à Rs 25 000 — et ça monte vite."
        datePublishedIso={DATE_PUBLISHED}
        dateLabel="4 septembre 2026"
        readingMinutes={4}
        hreflangSwitcher={{ label: "Read in English", to: "/blog/website-cost-mauritius" }}
        cta={{
          eyebrow: "Devis gratuit",
          title: "Envie qu'on regarde votre projet ensemble ?",
          subtitle:
            "On propose un devis gratuit sous 24h — location, achat ou accompagnement complet, on trouve la formule adaptée à votre budget et votre objectif.",
          label: "Demander mon devis gratuit",
          to: "/site-location-maurice",
        }}
      >
        <p>
          Ce n'est pas forcément injustifié. Mais ce que personne ne vous dit, c'est que ce
          modèle n'est pas le seul qui existe. Et surtout, qu'il n'est pas forcément le plus
          adapté à votre situation.
        </p>

        <h2 className="font-outfit text-2xl md:text-3xl text-white font-light tracking-tight pt-4">
          Pourquoi ça coûte aussi cher ?
        </h2>
        <p>
          Un <strong className="text-white">site web professionnel à Maurice</strong>, quand il est bien fait, demande du temps. Design,
          développement, textes, images, mise en ligne, tests sur mobile — une agence sérieuse
          y passe facilement plusieurs semaines. C'est ce temps de travail que vous payez en
          une seule fois quand vous achetez un site.
        </p>
        <p>
          Le problème ? Pour beaucoup d'entrepreneurs mauriciens — surtout ceux qui se
          lancent — sortir <strong className="text-white">Rs 25 000 à Rs 60 000 d'un coup</strong>, c'est un frein énorme. Résultat :
          ils repoussent, ils font sans, ou ils optent pour quelque chose de bâclé à petit
          prix qui ne leur ramène aucun client.
        </p>

        <h2 className="font-outfit text-2xl md:text-3xl text-white font-light tracking-tight pt-4">
          Ce que ça coûte vraiment en 2026
        </h2>
        <p>
          Il existe aujourd'hui deux façons d'avoir un site web professionnel à Maurice :
        </p>
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 md:p-7">
          <div className="text-xs uppercase tracking-[0.14em] text-[#FFB4BB] font-semibold">
            Option 1 · Achat classique
          </div>
          <p className="mt-3 text-white/75">
            Vous payez tout en une fois. Le site vous appartient à 100%. Comptez entre{" "}
            <strong className="text-white">Rs 25 000 et Rs 80 000</strong> selon la complexité —
            vitrine simple, e-commerce, fonctionnalités spécifiques. C'est le modèle
            traditionnel.
          </p>
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 md:p-7">
          <div className="text-xs uppercase tracking-[0.14em] text-[#FFB4BB] font-semibold">
            Option 2 · Location mensuelle
          </div>
          <p className="mt-3 text-white/75">
            Vous ne payez pas de gros montant au départ. Vous avez un{" "}
            <strong className="text-white">site professionnel livré en 15 à 30 jours</strong>,
            hébergement et nom de domaine inclus, pour un abonnement mensuel. C'est le modèle
            qu'on a développé chez EM Group — <strong className="text-white">à partir de Rs 3 500 par mois</strong> — parce qu'on
            a vu trop d'entrepreneurs mauriciens bloquer sur l'investissement de départ.
          </p>
        </div>

        <h2 className="font-outfit text-2xl md:text-3xl text-white font-light tracking-tight pt-4">
          Lequel choisir ?
        </h2>
        <p>
          Si vous avez le budget et que vous voulez être propriétaire de votre site dès le
          départ — <strong className="text-white">l'achat</strong> est fait pour vous.
        </p>
        <p>
          Si vous démarrez, si vous voulez tester avant d'investir massivement, ou si vous
          préférez étaler vos dépenses — <strong className="text-white">la location</strong> a tout son sens. Vous obtenez
          exactement le même résultat professionnel, sans sortir Rs 25 000 d'un coup.
        </p>

        <h2 className="font-outfit text-2xl md:text-3xl text-white font-light tracking-tight pt-4">
          Ce qu'on voit sur le terrain à Maurice
        </h2>
        <p>
          La plupart des entrepreneurs qu'on rencontre ne savaient pas que l'option location
          existait. Ils pensaient qu'un site web professionnel était forcément hors de portée.
          <strong className="text-white"> Rs 3 500 par mois, c'est souvent moins que leur facture téléphonique professionnelle.</strong>
        </p>
        <p>
          Un site web qui ne vous ramène aucun client, peu importe son prix, c'est de
          l'argent perdu. Ce qui compte, c'est qu'il soit bien construit, bien référencé sur
          Google, et qu'il donne envie à vos visiteurs de vous contacter.
        </p>

        <h2 className="font-outfit text-2xl md:text-3xl text-white font-light tracking-tight pt-4">
          En résumé
        </h2>
        <p>
          Un site web à Maurice coûte entre <strong className="text-white">Rs 3 500/mois en
          location</strong> et <strong className="text-white">Rs 25 000 à Rs 80 000 en achat</strong>. Le bon choix dépend de votre
          situation, de votre budget et de vos objectifs.
        </p>
        <p>
          Si vous voulez qu'on regarde ensemble ce qui correspond le mieux à votre projet —
          on propose un devis gratuit sous 24h.
        </p>
      </BlogLayout>
    </>
  );
}
